import { Container, Graphics, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { weaponGlyphKey } from '@/config/weapon-visuals';
import { t, type I18nKey } from '@/i18n';
import { STAT_KEYS, formatStat, type RunStats } from '@/core/records';
import { getActiveDevice } from '@/input/active-device';
import type { CardDef } from '@/upgrades/cards';
import { CARD_W, FACE_H, buildCardFace } from './card-face';
import { bodyText, chamferPanel, hexPath, label, targetBrackets } from './widgets';

/** Burn-picker cards are the REAL card face at full size (docs/12 K3,
 * polish 1: was ×0.74 → ~9.6 px text). Card and weapon panels match (N6). */
export const SAC_W = CARD_W;
export const SAC_H = FACE_H;
/** Weapon panel: same band / socket geometry as the card face */
const WEAPON_BAND = { x: 12, y: 6, h: 26 } as const;
const WEAPON_SOCKET = { y: 74, r: 32 } as const;
const WEAPON_NAME_Y = 124;
/** Charred-card crack paths, as fractions of the card (no asset dependency) */
const CRACKS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  [[0.18, 0.05], [0.29, 0.31], [0.23, 0.53], [0.39, 0.78]],
  [[0.76, 0.09], [0.65, 0.35], [0.8, 0.57], [0.7, 0.9]],
  [[0.1, 0.71], [0.34, 0.82], [0.51, 1]],
];

/** One burnable candidate on the game-over screen (card OR weapon — F3). */
export interface SacrificeItem {
  kind: 'card' | 'weapon';
  /** Offer index into the death-tax card offer (cards only) */
  offerIndex: number;
  card: CardDef | null;
  /** Owned stack count — shown on the face's level badge */
  level: number;
  weaponId: string | null;
  name: string;
  /** Frame color (card rarity / weapon rarity) */
  frame: number;
  /** Icon tint (card kind / weapon category) */
  accent: number;
  /** Armor units granted when burned PAST the mandatory tax */
  armorBonus: number;
}

/** Sacrifice candidate — full card face everywhere, charred when burned. */
export function buildSacrificeCard(
  item: SacrificeItem,
  selected: boolean,
  burned: boolean,
  showBonus: boolean,
): Container {
  const c = new Container();
  if (item.card) {
    const face = buildCardFace(item.card, item.level, {
      fill: burned ? 0x120a0e : undefined,
      stroke: selected ? COLORS.dangerRed : burned ? 0x3a2228 : undefined,
      strokeWidth: selected ? 4 : undefined,
    });
    if (burned) face.alpha = 0.45;
    c.addChild(face);
  } else {
    c.addChild(weaponFace(item, selected, burned));
  }

  if (burned) {
    const over = new Graphics();
    drawCracks(over);
    c.addChild(over);
    const stamp = label(t('gameover.burned'), 20, COLORS.dangerRed, 3);
    stamp.anchor.set(0.5);
    stamp.position.set(SAC_W / 2, SAC_H - 46);
    stamp.rotation = -0.12;
    c.addChild(stamp);
  } else if (showBonus) {
    // Past the tax, every extra burn repairs armor — value on the card
    const chip = new Graphics();
    chamferPanel(chip, SAC_W - 40, 30, {
      fill: 0x0f0c1a, stroke: COLORS.hudArmor, strokeWidth: 2, notch: 8, plain: true,
    });
    chip.position.set(20, SAC_H - 42);
    c.addChild(chip);
    const bonus = label(t('gameover.armorBonus', { n: item.armorBonus }), 15, COLORS.hudArmor, 1);
    bonus.anchor.set(0.5);
    bonus.position.set(SAC_W / 2, SAC_H - 27);
    c.addChild(bonus);
  }

  if (selected && !burned) {
    const br = new Graphics();
    targetBrackets(br, SAC_W, SAC_H, COLORS.dangerRed, 12, 4);
    c.addChild(br);
  }
  return c;
}

/** Weapon candidate styled like a card face (no CardDef behind it). */
function weaponFace(item: SacrificeItem, selected: boolean, burned: boolean): Container {
  const c = new Container();
  const panel = new Graphics();
  chamferPanel(panel, SAC_W, SAC_H, {
    fill: burned ? 0x120a0e : 0x161222,
    stroke: selected ? COLORS.dangerRed : burned ? 0x3a2228 : item.frame,
    strokeWidth: selected ? 4 : 3,
    notch: 12,
  });
  const b = WEAPON_BAND;
  panel.poly([b.x + 4, b.y, SAC_W - 8, b.y, SAC_W - 8, b.y + b.h, b.x, b.y + b.h])
    .fill({ color: burned ? 0x3a2228 : item.accent, alpha: 0.9 });
  c.addChild(panel);

  const tag = label(t('gameover.weaponTag'), 13, 0x0f0c1a, 2);
  tag.anchor.set(0.5);
  tag.position.set(SAC_W / 2, b.y + b.h / 2);
  c.addChild(tag);

  const socket = new Graphics();
  hexPath(socket, SAC_W / 2, WEAPON_SOCKET.y, WEAPON_SOCKET.r).fill(0x0f0c1a)
    .stroke({ color: burned ? 0x3a2228 : item.accent, width: 2.5 });
  c.addChild(socket);
  // The weapon's own glyph — the same one the hangar, HUD and crates show
  if (item.weaponId) {
    const glyph = new Sprite(getTexture(weaponGlyphKey(item.weaponId)));
    glyph.anchor.set(0.5);
    glyph.position.set(SAC_W / 2, WEAPON_SOCKET.y);
    if (burned) glyph.alpha = 0.35;
    c.addChild(glyph);
  }

  const name = bodyText(item.name, 16, burned ? 0x6a4a52 : 0xffffff, SAC_W - 24);
  name.anchor.set(0.5, 0);
  name.position.set(SAC_W / 2, WEAPON_NAME_Y);
  c.addChild(name);
  if (burned) c.alpha = 0.7;
  return c;
}

/** Left column of the game-over screen: run stats, try counter, seed. */
export function buildRunSummary(
  stats: RunStats,
  improved: ReadonlyArray<keyof RunStats>,
  seed: number,
  totalRuns: number,
  x = 60,
  width = 330,
): Container {
  const c = new Container();
  if (improved.length > 0) {
    const badge = label(t('gameover.newRecord'), 17, COLORS.playerYellow, 2);
    badge.position.set(x, 96);
    c.addChild(badge);
  }
  const startY = 136;
  STAT_KEYS.forEach((key, i) => {
    const isRecord = improved.includes(key);
    const color = isRecord ? COLORS.playerYellow : 0xd5d9de;
    const name = bodyText((isRecord ? '★ ' : '') + t(`stats.${key}` as I18nKey), 15, color);
    name.anchor.set(0, 0.5);
    name.position.set(x, startY + i * 26);
    const value = label(formatStat(key, stats[key]), 15, color, 0);
    value.anchor.set(1, 0.5);
    value.position.set(x + width, startY + i * 26);
    c.addChild(name, value);
  });
  const tries = label(t('gameover.totalRuns', { n: totalRuns }), 15, COLORS.uiAccent, 1);
  tries.position.set(x, startY + STAT_KEYS.length * 26 + 10);
  const seedText = bodyText(t('gameover.seed', { seed }), 12, 0x8a8f98);
  seedText.position.set(x, startY + STAT_KEYS.length * 26 + 36);
  c.addChild(tries, seedText);
  return c;
}

/** Device-aware control hint, bottom-anchored at (cx, cy). */
export function buildDeviceHint(cx: number, cy: number): Container {
  const device = getActiveDevice();
  const hintKey: I18nKey =
    device === 'gamepad' ? 'gameover.hint.gamepad'
    : device === 'touch' ? 'gameover.hint.touch'
    : 'gameover.hint.keyboard';
  const hint = bodyText(t(hintKey), 14, 0x6a7076);
  hint.anchor.set(0.5, 1);
  hint.position.set(cx, cy);
  return hint;
}

/** Jagged crack lines over a charred card. */
function drawCracks(g: Graphics): void {
  for (const path of CRACKS) {
    g.moveTo(path[0]![0] * SAC_W, path[0]![1] * SAC_H);
    for (let i = 1; i < path.length; i++) g.lineTo(path[i]![0] * SAC_W, path[i]![1] * SAC_H);
    g.stroke({ color: 0x0a0508, width: 3 });
  }
}
